import { ControlValidators, CustomValidators, daysRange, UnsubscriberClass, ValidationPropertyType, ValidationService, ValidationServiceHelper, StoreLoader, combineEither, isBeforeDay, addDays, subDays, startOfDay, formValueChanges, toggleAllFieldsDisability, syncFormValidations } from '@aston/foundation';
import { auditTime, delay, distinctUntilChanged, filter, map, skip, switchMap, take, takeUntil, tap, withLatestFrom } from 'rxjs/operators';
import { UntypedFormControl, Validators, FormsModule, ReactiveFormsModule, AbstractControl, ValidationErrors } from '@angular/forms';
import { ChangeDetectorRef, Component, EventEmitter, Input, OnInit, Output, inject } from '@angular/core';
import { BehaviorSubject, combineLatest, of, Subject, throwError } from 'rxjs';
import { toObservable } from '@angular/core/rxjs-interop';
import { TranslateModule } from '@ngx-translate/core';
import { AsyncPipe } from '@angular/common';
import { Store } from '@ngrx/store';

import * as DunningActionDetailStoreSelectors from '../../../root-store/dunning-action-detail-store/selectors';
import * as DunningActionDetailStoreActions from '../../../root-store/dunning-action-detail-store/actions';
import { observeFeature } from '../../../factor-features-module/stores/factor-features';
import { SuperDebtorPageStore } from '../../../debtors-module/stores/super-debtor-page';
import { MiddleDomainValidators } from '../../../shared-module/enums/validators';
import { DunningActionSpec } from '../../classes/action-specs/action-spec.class';
import { FormGroupTyped } from '../../../shared-module/infra/form-group.typed';
import { PaymentSchedulesService } from '../../../accounting-module/services';
import { ReferentialStore } from '../../../shared-module/stores/referential';
import { DunningActionAttributeType, DunningActionSubOrigin, DunningActionType } from '../../enums';
import { canBeCompleted } from '../../functions/completion.mapper.function';
import { ISuperDebtorIdentity } from '../../../debtors-module/models';
import { PostalType } from '../../../dunning-settings-module/enums';
import { IUserBase } from '../../../authentication-module/models';
import { DunningLevel } from '../../../shared-module/enums';
import { DunningActionsSpecsService } from '../../services';
import { IDunningActionForm } from '../../models';

import { DunningActionFormFieldContactsService } from './dunning-action-form-field-contacts/dunning-action-form-field-contacts.service';
import { DunningActionFormFieldsService } from './dunning-action-form.fields.service';
import { DUNNING_ACTION_FORM_COMPONENTS } from './dunning-action-form.components';


export type FormValidatorFieldsType = any; // TODO: fix me

@Component({
	selector: 'dunning-action-form',
	templateUrl: './dunning-action-form.component.html',
	styles: `
		form > div {
			height: calc(100vh - 290px);
		}
	`,
	providers: [
		DunningActionFormFieldContactsService,
		DunningActionFormFieldsService,
	],
	imports: [
		...DUNNING_ACTION_FORM_COMPONENTS,
		ReactiveFormsModule,
		TranslateModule,
		FormsModule,
		AsyncPipe,
	]
})
export class DunningActionFormComponent extends UnsubscriberClass implements OnInit {
	private validationService = inject(ValidationService);
	private dasService = inject(DunningActionsSpecsService);
	private store = inject(Store);
	private sdPageStore = inject(SuperDebtorPageStore);

	@Input() isEditMode = false;
	@Input() form: FormGroupTyped<IDunningActionForm>;

	@Input() hasDunningClearanceLevel = false;
	@Input() hasDunningMassActionClearanceLevel = false;
	@Output() dirtyChanged = new EventEmitter<boolean>(false);

	private cdr = inject(ChangeDetectorRef);
	private referential = inject(ReferentialStore);
	protected psService = inject(PaymentSchedulesService);
	protected formFieldsService = inject(DunningActionFormFieldsService);

	private keepFields = ['id', 'type', 'dunningLevel', 'accounting', 'realizationDate'];
	private validationHelper: ValidationServiceHelper;
	private rulesetFields: FormValidatorFieldsType;
	private get contactIsPayingCenter() { return this.form?.value?.isPayingCenter }

	actionTypeSpec: DunningActionSpec;
	initialDueDate: Date;
	dueDateMinDate: Date;
	disabledDates: Date[];
	
	DunningLevel = DunningLevel;
	DAAType = DunningActionAttributeType;
	DunningActionSubOrigin = DunningActionSubOrigin;

	get typeField() {
		return this.form.get('type');
	}

	get dunningLevelField() {
		return this.form.get('dunningLevel');
	}

	get accountingField() {
		return this.form.get('accounting');
	}

	get dueDateField() {
		return this.form.get('dueDate');
	}

	get subTypeField() {
		return this.form.get('subType');
	}

	get contactsField() {
		return this.form.controls['contacts'];
	}

	get isRealized(): boolean {
		const field = this.form.get('realizationDate');
		return field && field.value;
	}

	get isPayingCenterField() {
		return this.form.get('isPayingCenter');
	}

	get hasRequiredFields() {
		return !this.form.disabled
			&& this.hasDunningClearanceLevel
	}

	get contactsAreOptional() {
		return this.actionTypeSpec?.contactsAreOptional;
	}

	constructor() {
		super();
		this.formFieldsService.initFormComponent(this);
	}

	paymentScheduleInvoicingAmount$ = new BehaviorSubject<number>(null);
	currentActionType$ = new BehaviorSubject<DunningActionType>(null);
	currentActionTypeChanged$ = new Subject<DunningActionType>();

	currentAction$ = this.store.select(DunningActionDetailStoreSelectors.selectDunningAction);
	actionSubTypesSpecs$ = toObservable(this.referential.dunningActionSubTypes);

	assignablesOperators$ = this.store.select(DunningActionDetailStoreSelectors.selectAssignablesOperators);
	notifiablesUsers$ = this.store.select(DunningActionDetailStoreSelectors.selectNotifiablesUsersDisplay);

	actionTypeSpec$ = combineLatest([ this.currentActionType$, this.dasService.getSpecsStream()]).pipe(map(([type, specs]) => specs[type || DunningActionType.InternalFollowUp]));
	currentActionTypeIsImmutable$ = combineLatest([
		toObservable(this.referential.dunningActionImmutableTypes),
		this.currentActionType$,
	]).pipe(map(([immus, current]) => immus.includes(current)));

	superDebtorIdentity$ = this.sdPageStore.identityAsObservable();
	isPayingCenterEnabled$ = observeFeature('isPayingCenterEnabled');

	canBeCompleted$ = this.store.select(DunningActionDetailStoreSelectors.selectCanBeCompleted);
	canBeCompletedDelayed$ = this.canBeCompleted$.pipe(auditTime(300));
	canBeCompletedLoader = new StoreLoader(
		combineEither(
			this.store.select(DunningActionDetailStoreSelectors.selectSelectedContactsIsLoading),
			this.store.select(DunningActionDetailStoreSelectors.selectCanBeCompletedIsLoading),
		),
		this.store.select(DunningActionDetailStoreSelectors.selectCanBeCompletedError));

	shouldDisplayWarningOfNoContact$ = this.canBeCompletedDelayed$.pipe(
		map(_ => this.isEditMode
			&& this.form
			&& this.form.value.contacts
			&& this.form.value.contacts?.length === 0
			&& this.form.value.isPayingCenter === false
			&& this.typeField.value !== DunningActionType.PhoneCall
		));

	ngOnInit() {
		this.buildForm();

		this.updateFormWhenTypeChange().subscribe();
		this.disableFieldsForImmutableActions().subscribe()
		this.resetValidatorsWhenTypeChanges().subscribe();

		this.initFormValuesFromStoreData().subscribe()

		this.keepStoreDataSynced().subscribe();
		this.keepAccountingSelectionSynced().subscribe();
		this.syncPaymentCenterValuesSynced().subscribe()
		this.syncAccountingSelectionAsTouched().subscribe()
		this.syncDueDateAsTouched().subscribe()
		this.syncSubTypeAsTouched().subscribe()
	}

	private syncPaymentCenterValuesSynced() {
		return combineLatest([
			this.superDebtorIdentity$,
			this.isPayingCenterEnabled$,
			this.store.select(DunningActionDetailStoreSelectors.selectDunningAction).pipe(filter(Boolean), take(1))
		]).pipe(
			filter(([_, __, formValues]) => !formValues.realizationDate),
			auditTime(150),
			tap(([superDebtorIdentity, isPayingCenterEnabled, formValues]) => {
				// Bug 35802: Une AR de type téléphone créée sans l'option CP, celle-ci apparait cochée
				if (!(this.isPayingCenterField.value ?? true)) return;
				// Bug 32469: Duplication d'une ar avec CP n'active pas l'option CP
				if (isPayingCenterEnabled && superDebtorIdentity.payingCenterFunctionalId && (!this.contactsField.value?.length || formValues.isPayingCenter)) {
					this.isPayingCenterField.setValue(true);
				} else {
					this.isPayingCenterField.setValue(false);
				}
				this.store.dispatch(DunningActionDetailStoreActions.LoadEntityCanBeCompletedRequest(
					this.contactsField?.value || [],
					this.typeField.value || formValues.type,
					this.form.get('subType')?.value,
					superDebtorIdentity.id,
					this.isPayingCenterField.value
				));
			}),
			takeUntil(this.destroySubscriptions$)
		);
	}

	private initFormValuesFromStoreData() {
		return this.currentAction$.pipe(
			filter(Boolean),
			delay(1),
			take(1),
			withLatestFrom(
				this.assignablesOperators$,
				this.superDebtorIdentity$,
				this.isPayingCenterEnabled$
			),
			tap(([value, operators, sdIdentity, usingPC]: [IDunningActionForm, IUserBase[], ISuperDebtorIdentity, boolean]) => {
				this.initialDueDate = value.initialDueDate || value.dueDate || startOfDay(new Date);

				// past initial DueDate makes the field disabled
				// https://astonitf.visualstudio.com/LBP/_workitems/edit/8319
				if (!this.hasDunningMassActionClearanceLevel && value.id && isBeforeDay(this.initialDueDate, new Date)) {
					this.dueDateField.disable();
					this.cdr.detectChanges();

				} else if (this.hasDunningMassActionClearanceLevel) {
					// when the user has DunningMassActionClearanceLevel,
					// the minimum valid date is the initial date
					// but date between this min and today are not selectable
					const nextInitialDay = addDays(this.initialDueDate, 1);
					const yesterday = subDays(new Date(), 1);
					this.disabledDates = daysRange(nextInitialDay, yesterday);
				}

				// reset the operator value, if it has been set to a now-disabled user
				// Bug 19739: [ACC] [ACTION] : Absence de la valeur "Veuillez choisir" lors de l'ajout d'une action sur un client dont le chargé de recouvrement est inactif
				if (value.assignedOperator && !operators.find(op => op.id === +value.assignedOperator)) {
					value.assignedOperator = null;
				}

				if (value.isPayingCenter === undefined) {
					// Set isPayingCenter value ASAP for
					// Bug 20550: Acc [Mo-Clients-SuiviRelance] AR : Le toggle "Centre payeur" n'est pas activé par défaut
					value.isPayingCenter = !!(usingPC && sdIdentity.payingCenterId);
				}

				this.form.patchValue(value);
				this.cdr.detectChanges();

				// once set, listen for changes on dunningLevel
				// skip the first value on existing actions
				this.dunningLevelField.valueChanges.pipe(
					delay(0),
					distinctUntilChanged(),
					withLatestFrom(this.currentActionTypeIsImmutable$),
					filter(([, immutable]) => !immutable && !this.dunningLevelField.disabled),
					takeUntil(this.destroySubscriptions$)
				).subscribe(([param]) => {
					this.store.dispatch(DunningActionDetailStoreActions.UpdateSelectedLevel({ param }));
					this.accountingField.setValue([]);
				});

				// For PS step actions, fetch the step amount
				// User Story 22507: 📆[MO][Echéancier] - Création et cas de génération des actions de relance de type 'Suivi des promesses de paiement'
				this.psService.fetchMatchingPaymentScheduleStep(value as any).pipe(filter(Boolean),tap(step => this.paymentScheduleInvoicingAmount$.next(step.amount))).subscribe();

				// Only when not yet realized
				// Bug 20273: [ACC] [RELANCE-CENTRE PAYEUR] : Les informations du contact du centre payeur sont affichées sur l'action réalisée au niveau du client rattaché
				if (!value.realizationDate) this.keepCanBeCompletedSynced().subscribe();
			}),
			takeUntil(this.destroySubscriptions$)
		)
	}

	private disableFieldsForImmutableActions() {
		return this.currentActionTypeIsImmutable$.pipe(
			auditTime(100),
			tap(locked => {
				if (locked) toggleAllFieldsDisability(this.form, true);
				
				// these are always enabled until the action is realized
				if (this.hasDunningClearanceLevel && !this.isRealized) {
					[
						this.form.get('assignedOperator'),
						this.form.get('notifiedUsers'),
						this.form.get('comment'),
						this.form.get('origin'),
					]
						.filter(Boolean)
						.forEach(c => c.enable());
				}
			}),
			takeUntil(this.destroySubscriptions$),
		)
	}

	private resetValidatorsWhenTypeChanges() {
		return this.currentActionTypeChanged$.pipe(
			filter(Boolean),
			switchMap(type => MiddleDomainValidators[`Dunning${type}ActionFormValidator`]
				? of(`Dunning${type}ActionFormValidator`)
				: throwError('No available validator')
			),
			switchMap(validatorName => this.validationService.getHelper(MiddleDomainValidators[validatorName])),
			withLatestFrom(this.dasService.getSpecsStream()),
			tap(([[helper, fields]]) => {
				this.validationHelper = helper;
				this.rulesetFields = fields as FormValidatorFieldsType;
				if (fields.DueDate) {
					this.dueDateMinDate = (!this.hasDunningMassActionClearanceLevel || this.initialDueDate >= new Date()) ?
						new Date() : this.initialDueDate;
				}

				this.setFormValidators();
			}),
			takeUntil(this.destroySubscriptions$),
		)
	}

	private keepCanBeCompletedSynced() {
		return formValueChanges(this.form)
			.include('type', 'subType', 'dunningLevel', 'dueDate', 'contacts', 'isPayingCenter', 'accounting')
			.pipe(
				tap(({ fields }) => console.debug('[action-form] request can-be-completed after changing', fields)),
				tap(_ => {
					// Reset the CanBeComplete result before the delay
					this.store.dispatch(DunningActionDetailStoreActions.LoadCanBeCompletedSuccess({ entity: canBeCompleted() }));
				}),
				auditTime(600), // TODO: dirty.. this must pass after resetContactsWhenPayingCenterChanges()
				switchMap(_ => this.superDebtorIdentity$),
				tap(identity => {
					this.store.dispatch(DunningActionDetailStoreActions.LoadEntityCanBeCompletedRequest(
						this.contactsField?.value || [],
						this.typeField.value,
						this.form.get('subType')?.value,
						identity.id,
						this.isPayingCenterField.value
					));
				}),
				takeUntil(this.destroySubscriptions$)
			);
	}

	private keepAccountingSelectionSynced() {
		return combineLatest([
			this.typeField.valueChanges,
			this.dunningLevelField.valueChanges,
			this.accountingField.valueChanges,
			this.isPayingCenterField.valueChanges,
		]).pipe(
			auditTime(110),
			filter(([type]) => !!type),
			tap(_ => {
				this.store.dispatch(DunningActionDetailStoreActions.LoadSelectedAccountingRequest())
				this.store.dispatch(DunningActionDetailStoreActions.LoadAttachmentsRequest(this.form.get('id')?.value))
			}),
			takeUntil(this.destroySubscriptions$),
		);
	}

	private syncAccountingSelectionAsTouched() {
		return this.accountingField.valueChanges.pipe(
			auditTime(500),
			skip(1),
			tap(_ => this.form.markAsTouched()),
			takeUntil(this.destroySubscriptions$),
		);
	}

	private syncDueDateAsTouched() {
		return this.dueDateField.valueChanges.pipe(
			auditTime(500),
			skip(1),
			tap(_ => this.form.markAsTouched()),
			takeUntil(this.destroySubscriptions$),
		);
	}

	private syncSubTypeAsTouched() {
		return this.subTypeField.valueChanges.pipe(
			auditTime(500),
			skip(1),
			tap(_ => this.form.markAsTouched()),
			takeUntil(this.destroySubscriptions$),
		);
	}

	private updateFormWhenTypeChange() {
		return this.typeField.valueChanges.pipe(
			distinctUntilChanged(),
			withLatestFrom(
				this.dasService.getSpecsStream(),
				this.actionSubTypesSpecs$,
				this.currentAction$),
			tap(([type, specs, subTypes, dunningAction]) => {
				this.currentActionType$.next(type);
				this.updateFormForType(specs[type], subTypes[type], dunningAction);
				this.currentActionTypeChanged$.next(type);
			}),
			takeUntil(this.destroySubscriptions$),
		);
	}

	private keepStoreDataSynced() {
		return this.form.valueChanges.pipe(
			auditTime(100),
			tap(value => {
				this.dirtyChanged.next(this.form.dirty);

				// data normalization
				value = {
					...value,
					dunningTemplateId: value.dunningTemplateId ? +value.dunningTemplateId : null
				};

				// in html we have a select which returns only one id
				// and the api/form needs an array of contacts
				if (value.contacts) {
					value.contacts = [].concat(value.contacts);
				}

				this.store.dispatch(DunningActionDetailStoreActions.UpdateForm({ param: value }));	
			}),
			takeUntil(this.destroySubscriptions$),
		)
	}

	buildForm() {
		this.form.addControl('id', new UntypedFormControl({value: null, disabled: this.form.disabled}));
		this.form.addControl('type', new UntypedFormControl({value: null, disabled: this.form.disabled}));
		this.form.addControl('dunningLevel', new UntypedFormControl({value: null, disabled: this.form.disabled}));
		this.form.addControl('accounting', new UntypedFormControl({value: null, disabled: this.form.disabled}));
		this.form.addControl('comment', new UntypedFormControl({value: null, disabled: this.form.disabled}));
		this.form.addControl('dueDate', new UntypedFormControl({value: null, disabled: this.form.disabled}));
		this.form.addControl('subType', new UntypedFormControl({value: null, disabled: this.form.disabled}));
		this.form.addControl('isPayingCenter', new UntypedFormControl({value: true, disabled: this.form.disabled}));
		this.form.addControl('realizationDate', new UntypedFormControl({value: null, disabled: true}));
	}

	setFormValidators() {
		Object.keys(this.rulesetFields).forEach(key => {
			const fieldName = key[0].toLowerCase() + key.slice(1);
			if (!this.form.get(fieldName)) {
				console.error(`no such field ${fieldName}`)
				return
			}

			if (fieldName === 'dueDate') {
				if (!this.form.controls.id.value) {
					// Apply API validation rules (>= today)
					ControlValidators.createControlValidators(this.form.get(fieldName),
						[Validators.required, CustomValidators.afterNow(true), CustomValidators.validDate])
				} else {
					// If a date has been set, use exactInitialDateOrGreaterOrEqualsNow
					// To allow the initial date, or >= today, but not in between
					const rules = this.validationHelper.validationRules(this.rulesetFields[key])
						.filter(r => r.name !== ValidationPropertyType.AFTER_NOW && r.name !== ValidationPropertyType.AFTER_FACTOR_NOW);
					ControlValidators.createControlValidators(this.form.get(fieldName),
						[CustomValidators.exactInitialDateOrGreaterOrEqualsNow(this.initialDueDate), CustomValidators.validDate],
						rules);
				}

			} else if (fieldName === 'contacts' && this.contactIsPayingCenter) {
				// Bug 36269: Le bouton Sauvegarder ne s'active pas, en centre payeur
				this.contactsField.clearValidators();

			} else {
				this.validationHelper.control(this.form.get(fieldName), this.rulesetFields[key]);
			}
		});

		if (this.contactsAreOptional) {
			this.form.controls['contacts'].clearValidators();
			this.form.controls['contacts'].updateValueAndValidity();

		} else if (this.rulesetFields) {
			Object.keys(this.rulesetFields).forEach(key => {
				const fieldName = key[0].toLowerCase() + key.slice(1);
				if (fieldName === 'contacts') {
					this.validationHelper.control(this.form.get(fieldName), this.rulesetFields[key]);
				}
			})
		}

		// User Story 37250: Conditionner la réalisation automatique à la présence d'un modèle
		const templateField = this.form.get('dunningTemplateId')
		const isPlannedField = this.form.get('isPlanned')
		if (templateField && isPlannedField) {
			syncFormValidations(this.currentActionTypeChanged$, templateField, isPlannedField).subscribe(_ => {
				this.form.patchValue({
					dunningTemplateId: templateField.value,
					isPlanned: isPlannedField.value,
				}, { emitEvent: false })
				templateField.markAsDirty()
				isPlannedField.markAsDirty()
			});
			isPlannedField.addValidators(this.cannotBeCheckedWithoutTemplate.bind(this))
		}

		this.dunningLevelField.addValidators(Validators.required);
	}

	cannotBeCheckedWithoutTemplate(c: AbstractControl): ValidationErrors {
		return (c.value && !this.form.get('dunningTemplateId')?.value)
			? { required: true }
			: null
	}

	updateFormForType(spec: DunningActionSpec, subTypes: PostalType[], currentAction: IDunningActionForm) {
		this.actionTypeSpec = spec;

		// reset dunningTemplateId value when switching to Email / PostalMail
		// Bug 7913: [MO-RELANCE-ACTION] : Absence de la valeur "Veuillez choisir" par défaut
		if (this.form.controls.dunningTemplateId && spec.resetTemplateOnTypeChange) {
			this.form.controls.dunningTemplateId.setValue(null);
			this.form.controls.dunningTemplateId.markAsPristine();
		}

		const currentValues = this.form.value;
		const attributes = this.actionTypeSpec.attributes.filter(a => !!a.field);
		const attributeFields = attributes.map(a => a.field);

		// remove unwanted fields
		Object.keys(this.form.controls).forEach(ctrl => {
			if (!this.keepFields.includes(ctrl) && !attributeFields.includes(ctrl)) {
				this.form.removeControl(ctrl);
			}
		});

		// add new fields
		this.actionTypeSpec.attributes.filter(a => !!a.field).forEach(attribute => {
			const field = attribute.field.toString();
			if (!this.form.controls[field]) {
				this.form.addControl(field, new UntypedFormControl({value: null, disabled: this.form.disabled}));
				if (currentValues[field]) {
					this.form.controls[field].setValue(currentValues[field]);
				} else if (attribute.defaultValue !== undefined) {
					this.form.controls[field].setValue(attribute.defaultValue);
				}
			}
		});

		// some field shorthands
		const subTypeField = this.form.get('subType');

		// Reset subType field when its current value is not in the available subtypes
		if (subTypeField && (
			   (subTypes?.length && !subTypes.includes(subTypeField.value))
			|| (subTypeField.value && !subTypes?.length)
		)) {
			subTypeField.setValue(null);
		}

		// Use the first available subtype if none is selected
		if (subTypes && subTypes.length && subTypeField && !subTypeField.value) {
			subTypeField.setValue(subTypes[0])
		}

		// lock fields according to spec
		// User Story 31233: [Facturation] Création automatique d'une action de relance correspondante au mode d'envoi sélectionné
		this.actionTypeSpec.lockedFields(currentAction).forEach(field => this.form.get(field)?.disable());
	}
}
